import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Download, ShieldCheck, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import SearchBar from "@/components/business/SearchBar";
import PageHeader from "@/components/business/PageHeader";

export default function AuditTrail() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState({ q: "", action: "", entity_type: "", date_from: "", date_to: "" });
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const load = async () => {
    const params = new URLSearchParams({ skip, limit });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const { data } = await api.get(`/audit/events?${params}`);
    setRows(data.items); setTotal(data.total);
  };

  useEffect(() => { load(); api.get("/audit/summary").then(({ data }) => setSummary(data)); /* eslint-disable-next-line */ }, [skip]);
  useEffect(() => { setSkip(0); load(); /* eslint-disable-next-line */ }, [filters]);

  const download = async (fmt) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    try {
      const resp = await api.get(`/audit/export.${fmt}?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `audit-trail.${fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title eyebrow="Compliance">Audit Trail</PageHeader.Title>
          <PageHeader.Actions>
            <Button variant="outline" onClick={() => download("csv")} data-testid="export-csv"><Download className="h-4 w-4 mr-2" />CSV</Button>
            <Button variant="outline" onClick={() => download("pdf")} data-testid="export-pdf"><Download className="h-4 w-4 mr-2" />PDF</Button>
          </PageHeader.Actions>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Immutable, append-only. Every meaningful action across the platform is recorded here.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-5 border-border">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Total events</div>
            <div className="mt-2 font-display text-3xl font-bold num-rupee">{summary.total ?? 0}</div>
          </Card>
          <Card className="p-5 border-border">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Last 24h</div>
            <div className="mt-2 font-display text-3xl font-bold num-rupee">{summary.last_24h ?? 0}</div>
          </Card>
          <Card className="p-5 border-border lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Top actions</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(summary.top_actions || []).slice(0, 6).map(a => (
                <Badge key={a.action} variant="outline" className="font-mono text-[10px]">{a.action}<span className="ml-1.5 opacity-70">×{a.count}</span></Badge>
              ))}
              {(summary.top_actions || []).length === 0 && <div className="text-xs text-muted-foreground">No events yet.</div>}
            </div>
          </Card>
        </div>

        <Card className="mt-6 p-4 border-border" data-testid="audit-filters">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Search</Label>
              <SearchBar value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Action, actor, entity id…" className="mt-1.5" inputClassName="h-10" data-testid="audit-search" />
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Input value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} placeholder="e.g. filing.prepared" className="h-10 mt-1.5 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs">Entity type</Label>
              <Select value={filters.entity_type || "all"} onValueChange={(v) => setFilters({ ...filters, entity_type: v === "all" ? "" : v })}>
                <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="filing">Filing</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-10 mt-1.5" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-10 mt-1.5" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="mt-6 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                <th className="text-left p-3 w-40">Timestamp</th>
                <th className="text-left p-3">Action</th>
                <th className="text-left p-3">Actor</th>
                <th className="text-left p-3">Entity</th>
                <th className="text-left p-3">Prev → New</th>
                <th className="text-left p-3">IP</th>
              </tr>
            </thead>
            <tbody data-testid="audit-rows">
              {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">No events for these filters.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30 align-top">
                  <td className="p-3 text-xs text-muted-foreground font-mono">{new Date(r.at).toLocaleString()}</td>
                  <td className="p-3 font-mono text-xs">{r.action}</td>
                  <td className="p-3">
                    <div className="text-xs">{r.actor_email || "—"}</div>
                    <div className="text-[10px] text-muted-foreground">{r.actor_role}</div>
                  </td>
                  <td className="p-3">
                    <div className="text-xs">{r.entity_type}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{(r.entity_id || "").slice(0, 12)}</div>
                  </td>
                  <td className="p-3 text-[11px] font-mono max-w-xs truncate">
                    <div className="text-rose-700 dark:text-rose-400 truncate">{r.prev_value ? JSON.stringify(r.prev_value) : ""}</div>
                    <div className="text-emerald-700 dark:text-emerald-400 truncate">{r.new_value ? JSON.stringify(r.new_value) : ""}</div>
                  </td>
                  <td className="p-3 text-[10px] text-muted-foreground font-mono">{r.ip || "—"}<div className="truncate max-w-[10rem]" title={r.user_agent}>{(r.user_agent || "").slice(0, 22)}…</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div className="mt-4 flex items-center justify-between text-xs">
          <div className="text-muted-foreground">Showing {rows.length > 0 ? skip + 1 : 0}–{skip + rows.length} of {total}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" disabled={skip + rows.length >= total} onClick={() => setSkip(skip + limit)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
